import React, { useEffect, useMemo, useRef, useState } from "react";
import { ActionLoanMenu } from "@/components/Holizontal/ActionLoanMenu";
import { apiFetch } from "@/lib/api";
import { helper } from "@/lib/helper";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, HandCoins } from "lucide-react";
import { useTranslation } from "react-i18next";

type Branch = { id: number; name: string };
type Bdo = { id: number; name: string };

type LoanRow = {
  id: number;
  loan_number?: string | number;

  BRANCH?: string;
  PRODUCT?: string;

  fname?: string;
  mname?: string;
  sname?: string;
  phone_number?: string;

  USERNAME?: string;
  USERNAME2?: string;

  disbursed_date?: string;
  disbursed_amount?: number | string;

  LOAN?: number | string;
  PAYMENT?: number | string;
  BALANCE?: number | string;

  loan_days?: number | string;
  loan_status?: "Active" | "Closed" | "Defaulted" | string;

  due_date?: string;
};

type ApiResponse = {
  data: LoanRow[];
  meta: {
    per_page: number;
    next_cursor: string | null;
    prev_cursor: string | null;
    has_more: boolean;
  };
  anchor_id: number | null;

  // keep compatible with your backend
  branches?: Branch[];
  bdos?: Bdo[];
};

type MetaListResponse<T> = { data: T[] };

const PER_PAGE = 10;
const API_PATH = "/actions/loans/active";

function parseNum(v: any) {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const n = Number(String(v).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function useDebounced<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function statusChipClass(s?: string) {
  const v = (s ?? "").toLowerCase();
  if (v === "active") return "bg-emerald-500/10 border-emerald-500/20 text-emerald-600";
  if (v === "closed") return "bg-muted border-border text-muted-foreground";
  if (v === "defaulted") return "bg-destructive/10 border-destructive/20 text-destructive";
  return "bg-muted border-border text-muted-foreground";
}

export default function LoansPage() {
  // filters
  const [q, setQ] = useState("");
  const qDebounced = useDebounced(q, 350);

  const [branchId, setBranchId] = useState("");
  const [bdoId, setBdoId] = useState("");
  const [loanStatus, setLoanStatus] = useState<string>(""); // "", "1", "5", "6"

  // data
  const [bootLoading, setBootLoading] = useState(true);
  const [loading, setLoading] = useState(false); // list loading
  const [error, setError] = useState<string | null>(null);

  const [rows, setRows] = useState<LoanRow[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [bdos, setBdos] = useState<Bdo[]>([]);

  // meta loaders
  const [metaLoading, setMetaLoading] = useState(false);
  const [bdoLoading, setBdoLoading] = useState(false);

  // cursor paging
  const [cursor, setCursor] = useState<string | null>(null);
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [prevCursor, setPrevCursor] = useState<string | null>(null);

  // scroll target
  const listTopRef = useRef<HTMLDivElement | null>(null);

  // ✅ pagination smooth-scroll AFTER new data renders (prevents “freeze/jump”)
  const [pendingScrollTop, setPendingScrollTop] = useState(false);
  useEffect(() => {
    if (!pendingScrollTop) return;
    if (loading) return;

    requestAnimationFrame(() => {
      listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      // or: window.scrollTo({ top: 0, behavior: "smooth" });
    });
    setPendingScrollTop(false);
  }, [pendingScrollTop, loading, rows.length]);

  // ✅ Load branches once (so branch select works even if main API doesn’t return branches)
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setMetaLoading(true);
        const res = await apiFetch<MetaListResponse<Branch>>("/actions/loans/meta/branches");
        if (!alive) return;
        setBranches(res.data ?? []);
      } catch {
        if (!alive) return;
        setBranches([]);
      } finally {
        if (alive) setMetaLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // ✅ Branch first -> then load BDOs (and reset BDO on change)
  useEffect(() => {
    let alive = true;

    setBdoId("");
    setBdos([]);

    if (!branchId) return;

    (async () => {
      try {
        setBdoLoading(true);
        const res = await apiFetch<MetaListResponse<Bdo>>(`/actions/loans/meta/bdos?branch_id=${branchId}`);
        if (!alive) return;
        setBdos(res.data ?? []);
      } catch {
        if (!alive) return;
        setBdos([]);
      } finally {
        if (alive) setBdoLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [branchId]);

  // reset paging when filters change
  useEffect(() => {
    setCursor(null);
    setAnchorId(null);
  }, [qDebounced, branchId, bdoId, loanStatus]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", String(PER_PAGE));

    if (qDebounced.trim()) params.set("q", qDebounced.trim());
    if (branchId) params.set("branch_id", branchId);

    // ✅ only send bdo_id if branch selected (avoid wrong filtering)
    if (branchId && bdoId) params.set("bdo_id", bdoId);

    if (loanStatus) params.set("loan_status", loanStatus);

    if (cursor) params.set("cursor", cursor);
    if (anchorId != null) params.set("anchor_id", String(anchorId));

    return params.toString();
  }, [qDebounced, branchId, bdoId, loanStatus, cursor, anchorId]);

  async function load(isBoot = false) {
    if (isBoot) setBootLoading(true);
    setLoading(true);
    setError(null);

    try {
      const json = await apiFetch<ApiResponse>(`${API_PATH}?${queryString}`);

      setRows(json.data ?? []);

      // keep compatible if backend returns these
      if ((json.branches ?? []).length) setBranches(json.branches ?? []);
      if ((json.bdos ?? []).length && !branchId) setBdos(json.bdos ?? []); // only if you want

      setNextCursor(json.meta?.next_cursor ?? null);
      setPrevCursor(json.meta?.prev_cursor ?? null);
      setAnchorId((prev) => (prev == null ? json.anchor_id ?? null : prev));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
      setRows([]);
      setNextCursor(null);
      setPrevCursor(null);
    } finally {
      setLoading(false);
      if (isBoot) setBootLoading(false);
    }
  }

  // boot load
  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reload on filters/cursor
  useEffect(() => {
    if (bootLoading) return;
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  const totals = useMemo(() => {
    const totalLoan = rows.reduce((s, r) => s + parseNum(r.LOAN), 0);
    const totalPaid = rows.reduce((s, r) => s + parseNum(r.PAYMENT), 0);
    const totalBal = rows.reduce((s, r) => s + parseNum(r.BALANCE), 0);
    return { count: rows.length, totalLoan, totalPaid, totalBal };
  }, [rows]);

  const resetAll = () => {
    setQ("");
    setBranchId("");
    setBdoId("");
    setLoanStatus("");
    setCursor(null);
    setAnchorId(null);
  };

  // ✅ scroll AFTER new rows render
  const goPrev = () => {
    if (!prevCursor || loading) return;
    setPendingScrollTop(true);
    setCursor(prevCursor);
  };

  const goNext = () => {
    if (!nextCursor || loading) return;
    setPendingScrollTop(true);
    setCursor(nextCursor);
  };

  const fullName = (r: LoanRow) =>
    `${r.fname ?? ""} ${r.mname ?? ""} ${r.sname ?? ""}`.replace(/\s+/g, " ").trim() || "—";

  const loanNo = (r: LoanRow) => (r.loan_number ?? r.id).toString();

  const { t } = useTranslation();

  return (
    <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-4 space-y-4">
      <header className="border-b border-border bg-card sticky top-0 z-30 mb-3">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <HandCoins className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">{t("loans.title")}</h1>
              <p className="text-xs text-muted-foreground">Active, Closed, and Defaulted loans (disbursed).</p>
            </div>
          </div>
        </div>
      </header>

      <ActionLoanMenu />

      {/* ✅ Top loader bar (same as your other pages) */}
      {loading && (
        <div className="sticky top-0 z-10">
          <div className="h-1 w-full bg-muted overflow-hidden rounded">
            <motion.div
              className="h-full bg-primary"
              initial={{ x: "-100%" }}
              animate={{ x: "100%" }}
              transition={{ repeat: Infinity, duration: 1.0, ease: "linear" }}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Search</div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name / phone / loan..."
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Branch</div>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              disabled={metaLoading || loading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                (metaLoading || loading) && "opacity-60 cursor-not-allowed"
              )}
            >
              <option value="">{metaLoading ? "Loading branches..." : "All Branches"}</option>
              {branches.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">BDO</div>
            <select
              value={bdoId}
              onChange={(e) => setBdoId(e.target.value)}
              disabled={!branchId || bdoLoading || loading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                (!branchId || bdoLoading || loading) && "opacity-60 cursor-not-allowed"
              )}
            >
              <option value="">
                {!branchId ? "Select Branch first" : bdoLoading ? "Loading BDOs..." : "All BDOs"}
              </option>
              {bdos.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Status</div>
            <select
              value={loanStatus}
              onChange={(e) => setLoanStatus(e.target.value)}
              disabled={loading}
              className={cn(
                "h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring",
                loading && "opacity-60 cursor-not-allowed"
              )}
            >
              <option value="">All</option>
              <option value="1">Active</option>
              <option value="5">Closed</option>
              <option value="6">Defaulted</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
          <div className="flex flex-wrap gap-2 text-sm">
            <div className="px-3 py-1 rounded-full bg-muted">
              Rows: <span className="font-semibold">{totals.count}</span>
            </div>
            <div className="px-3 py-1 rounded-full bg-muted">
              Total Loan: <span className="font-semibold">{helper.formatMoneyKES(totals.totalLoan)}</span>
            </div>
            <div className="px-3 py-1 rounded-full bg-muted">
              Paid: <span className="font-semibold">{helper.formatMoneyKES(totals.totalPaid)}</span>
            </div>
            <div className="px-3 py-1 rounded-full bg-muted">
              Balance: <span className="font-semibold">{helper.formatMoneyKES(totals.totalBal)}</span>
            </div>
            {loading && <div className="px-3 py-1 rounded-full bg-muted text-muted-foreground">Loading...</div>}
          </div>

          <button
            onClick={resetAll}
            disabled={loading}
            className={cn(
              "h-10 px-3 rounded-md border border-border text-sm font-medium",
              loading ? "opacity-60 cursor-not-allowed" : "hover:bg-muted"
            )}
          >
            Reset
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}
      </div>

      {/* Listing */}
      <div className="bg-card border border-border rounded-xl p-3 sm:p-4 relative">
        <div ref={listTopRef} />

        {/* ✅ Overlay loader */}
        {loading && (
          <div className="absolute inset-0 z-10 rounded-xl bg-background/60 backdrop-blur-[1px] flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
              <motion.span
                className="h-4 w-4 rounded-full border-2 border-muted-foreground border-t-transparent"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }}
              />
              Loading loans...
            </div>
          </div>
        )}

        {/* Desktop table */}
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="min-w-[1000px] w-full  text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Branch</th>
                  <th className="px-4 py-3 font-semibold">Disbursed Date</th>
                  <th className="px-4 py-3 font-semibold">Name & Phone</th>
                  <th className="px-4 py-3 font-semibold">Loan ID</th>
                  <th className="px-4 py-3 font-semibold">Disbursed</th>
                  <th className="px-4 py-3 font-semibold">Total Loan</th>
                  <th className="px-4 py-3 font-semibold">Paid</th>
                  <th className="px-4 py-3 font-semibold">Balance</th>
                  <th className="px-4 py-3 font-semibold">Running Days</th>
                  <th className="px-4 py-3 font-semibold">Due Date</th>
                  <th className="px-4 py-3 font-semibold">BDO</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>

              <tbody>
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-10 text-center text-muted-foreground">
                      No loans found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3">{r.BRANCH ?? "-"}</td>
                      <td className="px-4 py-3">{helper.compactDate(r.disbursed_date)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{fullName(r)}</div>
                        <div className="text-xs text-muted-foreground">{r.phone_number ?? "-"}</div>
                      </td>
                      <td className="px-4 py-3 font-medium text-primary">{loanNo(r)}</td>
                      <td className="px-4 py-3">{helper.formatMoneyKES(parseNum(r.disbursed_amount))}</td>
                      <td className="px-4 py-3">{helper.formatMoneyKES(parseNum(r.LOAN))}</td>
                      <td className="px-4 py-3">{helper.formatMoneyKES(parseNum(r.PAYMENT))}</td>
                      <td className="px-4 py-3">{helper.formatMoneyKES(parseNum(r.BALANCE))}</td>
                      <td className="px-4 py-3">{String(r.loan_days ?? "-")}</td>
                      <td className="px-4 py-3">{r.due_date ? helper.compactDate(r.due_date) : "-"}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.USERNAME ?? "-"}</div>
                        {r.USERNAME2 ? <div className="text-xs text-muted-foreground">{r.USERNAME2}</div> : null}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn("inline-flex px-2 py-1 rounded-full text-xs border", statusChipClass(r.loan_status))}>
                          {r.loan_status ?? "-"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Bottom pagination */}
          <div className="mt-4 flex items-center justify-between gap-2">
            <button
              onClick={goPrev}
              disabled={!prevCursor || loading}
              className={cn(
                "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !prevCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              Prev
            </button>

            <button
              onClick={goNext}
              disabled={!nextCursor || loading}
              className={cn(
                "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !nextCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {!loading && rows.length === 0 ? (
            <div className="rounded-lg border border-border p-5 text-center text-sm text-muted-foreground">
              No loans found.
            </div>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="rounded-xl border border-border bg-background p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{fullName(r)}</div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-1">
                      <span className="truncate">{r.phone_number ?? "-"}</span>
                      <span>•</span>
                      <span className="truncate">{r.BRANCH ?? "-"}</span>
                      <span>•</span>
                      <span className="truncate">Loan #{loanNo(r)}</span>
                    </div>
                  </div>

                  <span className={cn("shrink-0 inline-flex px-2 py-1 rounded-full text-xs border", statusChipClass(r.loan_status))}>
                    {r.loan_status ?? "-"}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Disbursed</div>
                    <div className="font-bold">{helper.formatMoneyKES(parseNum(r.disbursed_amount))}</div>
                    <div className="text-xs text-muted-foreground mt-1">{helper.compactDate(r.disbursed_date)}</div>
                  </div>

                  <div>
                    <div className="text-xs text-muted-foreground">Balance</div>
                    <div className="font-bold">{helper.formatMoneyKES(parseNum(r.BALANCE))}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Paid: {helper.formatMoneyKES(parseNum(r.PAYMENT))}
                    </div>
                  </div>

                  <div className="col-span-2">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        {r.USERNAME && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs font-medium">
                            👤 {r.USERNAME}
                          </span>
                        )}
                        {r.USERNAME2 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs font-medium">
                            👤 {r.USERNAME2}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        ⏳ <span>Running:</span>
                        <span className="font-semibold text-foreground">{String(r.loan_days ?? "-")} days</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Bottom pagination (mobile) */}
          <div className="mt-4 flex items-center justify-between gap-2">
            <button
              onClick={goPrev}
              disabled={!prevCursor || loading}
              className={cn(
                "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !prevCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              <ArrowLeft className="h-4 w-4" />
              Prev
            </button>

            <button
              onClick={goNext}
              disabled={!nextCursor || loading}
              className={cn(
                "h-10 px-4 rounded-md border border-border text-sm font-medium inline-flex items-center gap-2",
                !nextCursor || loading ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              )}
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}